import { SearchOption } from "@tc/configurability"

/**
 * Builds Typesense-compatible filter string from recipe configuration
 * Handles multi-search scenarios and proper value escaping
 */
export const buildTypesenseFilter = (
  searchOption: SearchOption,
  additionalFilters?: string[],
): string => {
  const filters: string[] = []

  // Add precondition filters from recipe configuration
  if (searchOption.precondition) {
    const preconditionFilters = Array.isArray(searchOption.precondition)
      ? searchOption.precondition
      : [searchOption.precondition]

    preconditionFilters.forEach((condition) => {
      // Map enum operators to Typesense operators
      const operatorMap: Record<string, string> = {
        equals: "=",
        notEquals: "!=",
        greaterThan: ">",
        lessThan: "<",
        greaterThanOrEqual: ">=",
        lessThanOrEqual: "<=",
      }
      
      const operator = operatorMap[condition.operator] ?? condition.operator ?? "="
      let value = condition.value

      // Escape string values that contain spaces or special characters
      if (typeof value === "string") {
        if (value.includes(" ") || value.includes(":") || value.includes("&")) {
          value = `"${value.replace(/"/g, '\\"')}"`
        }
      }

      filters.push(`${condition.fieldRef}:${operator}${value}`)
    })
  }

  // Add any additional filters
  if (additionalFilters) {
    filters.push(...additionalFilters)
  }

  return filters.join(" && ")
}

/**
 * Validates filter syntax for Typesense compatibility
 */
export const validateTypesenseFilter = (filter: string): boolean => {
  try {
    // Basic validation - check for balanced quotes and valid operators
    const quoteCount = (filter.match(/"/g) || []).length
    if (quoteCount % 2 !== 0) return false

    // Check for valid operators
    const validOperators = ["=", "!=", ">", "<", ">=", "<=", ":"]
    const hasValidOperator = validOperators.some((op) => filter.includes(op))

    return hasValidOperator
  } catch {
    return false
  }
}
